import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../context/PermissionsContext';
import { inventoryAPI, propertiesAPI, apartmentsAPI, documentsAPI, inspectionsAPI, fileViewUrl } from '../services/api';
import Modal from '../components/common/Modal';
import { toast } from '../components/common/Toast';
import { Package, Plus, Search, AlertTriangle, Edit2, Trash2, RefreshCw, FileText, Save, Paperclip, Film, Image, File } from 'lucide-react';
import { fmt, errMsg } from '../utils/helpers';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const UNIT_CATS   = ['Kitchen','Bathroom','Bedroom','Closets','Doors & Windows','Electrical','Lighting','Plumbing','General'];
const SUPPLY_CATS = ['Maintenance Supplies','Paint & Supplies','Janitorial','Safety','Security','Landscaping','Appliances','Other'];
const ALL_CATS    = [...UNIT_CATS, ...SUPPLY_CATS];
const CONDITIONS  = ['good','fair','poor','damaged','missing'];

const CAT_KEY = {
  'Kitchen':'catKitchen','Bathroom':'catBathroom','Bedroom':'catBedroom',
  'Closets':'catClosets','Doors & Windows':'catDoorsWindows','Electrical':'catElectrical',
  'Lighting':'catLighting','Plumbing':'catPlumbing','General':'catGeneral',
  'Maintenance Supplies':'catMaintenanceSupplies','Paint & Supplies':'catPaintSupplies',
  'Janitorial':'catJanitorial','Safety':'catSafety','Security':'catSecurity',
  'Landscaping':'catLandscaping','Appliances':'catAppliances','Other':'catOther',
};
const COND_KEY   = { good:'condGood', fair:'condFair', poor:'condPoor', damaged:'condDamaged', missing:'condMissing' };
const COND_COLOR = { good:'var(--emerald)', fair:'var(--amber)', poor:'#f97316', damaged:'var(--rose)', missing:'var(--ink-20)' };

const EMPTY = { propertyId:'', apartmentId:'', name:'', category:'Kitchen', condition:'good', quantity:'1', unit:'units', minStock:'0', cost:'', supplier:'', location:'', notes:'' };

const UNIT_TEMPLATE = [
  { nameKey:'tplKitchenCabinets',   category:'Kitchen',           condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplKitchenFaucet',     category:'Kitchen',           condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplKitchenSink',       category:'Kitchen',           condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplRefrigerator',      category:'Appliances',        condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplStoveRange',        category:'Appliances',        condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplToilet',            category:'Bathroom',          condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplBathroomFaucet',    category:'Bathroom',          condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplShowerHead',        category:'Bathroom',          condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplBathroomMirror',    category:'Bathroom',          condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplCloset',            category:'Closets',           condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplEntryDoor',         category:'Doors & Windows',   condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplBedroomDoor',       category:'Doors & Windows',   condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplWindows',           category:'Doors & Windows',   condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplLightSwitches',     category:'Electrical',        condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplElectricalOutlets', category:'Electrical',        condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplCeilingLight',      category:'Lighting',          condition:'good', quantity:1, unit:'units', notes:'' },
  { nameKey:'tplSmokeDetector',     category:'Safety',            condition:'good', quantity:1, unit:'units', notes:'' },
];

function tCat(t, cat) { return t(CAT_KEY[cat] || cat); }
function tCond(t, cond) { return t(COND_KEY[cond] || cond); }

// ── Signature Canvas ────────────────────────────────────────────
function SignatureCanvas({ label, canvasRef }) {
  const [drawing, setDrawing] = useState(false);
  const lastPos = useRef(null);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * scaleX, y: (src.clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    lastPos.current = getPos(e, canvasRef.current);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e, canvasRef.current);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 11, color: 'var(--ink-40)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <canvas ref={canvasRef} width={400} height={100}
        style={{ border: '1px solid var(--border)', borderRadius: 8, cursor: 'crosshair', touchAction: 'none', width: '100%', height: 100, background: '#fafafa' }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <button type="button" onClick={clear}
        style={{ fontSize: 11, color: 'var(--rose)', background: 'none', border: 'none', cursor: 'pointer', marginTop: 2, padding: 0 }}>
        ✕ Clear
      </button>
    </div>
  );
}

// ── Unit Report View ────────────────────────────────────────────
function UnitReportView({ data, t, inspectorRef, tenantRef }) {
  const { apartment, grouped } = data;
  const cats = Object.keys(grouped).sort();
  const allItems = Object.values(grouped).flat();
  const condCounts = CONDITIONS.reduce((acc, c) => ({ ...acc, [c]: allItems.filter(i => i.condition === c).length }), {});

  return (
    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} id="unit-report-print">
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--brand)' }}>PropManager</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-80)', letterSpacing: '.08em' }}>MERCURY</div>
        <div style={{ fontSize: 11, color: 'var(--ink-40)', letterSpacing: '.1em', marginTop: 4, textTransform: 'uppercase' }}>{t('unitInspectionReport')}</div>
      </div>

      {/* Unit info */}
      <div style={{ display: 'flex', justifyContent: 'space-between', background: 'var(--surface-2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12 }}>
        <div><span style={{ color: 'var(--ink-40)' }}>{t('unit')}: </span><strong>#{apartment.unit}</strong></div>
        <div><span style={{ color: 'var(--ink-40)' }}>{t('property')}: </span><strong>{apartment.propertyId?.name}</strong></div>
        <div><span style={{ color: 'var(--ink-40)' }}>{t('date')}: </span><strong>{new Date().toLocaleDateString()}</strong></div>
      </div>

      {/* Condition summary */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {CONDITIONS.filter(c => condCounts[c] > 0).map(c => (
          <div key={c} style={{ background: COND_COLOR[c] + '18', border: `1px solid ${COND_COLOR[c]}40`, borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
            <span style={{ color: COND_COLOR[c], fontWeight: 700 }}>{condCounts[c]}</span>
            <span style={{ color: 'var(--ink-60)', marginLeft: 4 }}>{tCond(t, c)}</span>
          </div>
        ))}
      </div>

      {/* Items by category */}
      <div style={{ borderTop: '2px dashed var(--border)', paddingTop: 12, marginBottom: 16 }}>
        {cats.map(cat => (
          <div key={cat} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>
              {tCat(t, cat)}
            </div>
            {grouped[cat].map(item => (
              <div key={item._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{item.name}</span>
                  {item.notes && <span style={{ color: 'var(--ink-40)', marginLeft: 8, fontSize: 11 }}>{item.notes}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 11, color: 'var(--ink-40)' }}>×{item.quantity}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: COND_COLOR[item.condition] }}>
                    {tCond(t, item.condition)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ))}
        {cats.length === 0 && <p style={{ color: 'var(--ink-40)', textAlign: 'center', padding: 20 }}>{t('noUnitItems')}</p>}
      </div>

      {/* Signatures */}
      <div style={{ borderTop: '1px dashed var(--border)', paddingTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-60)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>{t('signatureSection')}</div>
        <div style={{ display: 'flex', gap: 16 }}>
          <SignatureCanvas label={t('inspectorSignature')} canvasRef={inspectorRef} />
          <SignatureCanvas label={t('tenantSignature')}   canvasRef={tenantRef} />
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: 14, fontSize: 10, color: 'var(--ink-20)' }}>
        {t('reportGeneratedOn')} {new Date().toLocaleString()}
      </div>
    </div>
  );
}

// ── Attachment helpers ──────────────────────────────────────────
function attIcon(mimeType) {
  if (!mimeType) return <File size={20} color="var(--ink-40)"/>;
  if (mimeType.startsWith('image/')) return <Image size={20} color="var(--brand)"/>;
  if (mimeType.startsWith('video/')) return <Film  size={20} color="var(--rose)"/>;
  return <File size={20} color="var(--ink-40)"/>;
}

function AttachmentManager({ item, t, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [deleting,  setDeleting]  = useState(null);
  const fileRef = useRef();

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const { data } = await inventoryAPI.addAttachments(item._id, fd);
      toast.success(t('attachmentAdded'));
      onUpdated({ ...item, attachments: data.data });
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (key) => {
    setDeleting(key);
    try {
      const { data } = await inventoryAPI.deleteAttachment(item._id, key);
      toast.success(t('attachmentDeleted'));
      onUpdated({ ...item, attachments: data.data });
    } catch (err) { toast.error(errMsg(err)); }
    finally { setDeleting(null); }
  };

  const atts = item.attachments || [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: 'var(--ink-40)' }}>{atts.length} {t('attachments').toLowerCase()}</div>
        <label style={{ cursor: 'pointer' }}>
          <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.gif,.mp4,.mov,.avi,.mkv,.heic"
            onChange={handleUpload}/>
          <span className="btn btn-secondary" style={{ fontSize: 12 }}>
            {uploading ? '…' : <><Plus size={13}/> {t('uploadFiles')}</>}
          </span>
        </label>
      </div>

      {atts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--ink-20)', fontSize: 13 }}>
          <Paperclip size={28} style={{ margin: '0 auto 8px', opacity: .4 }}/>
          <div>{t('noAttachments')}</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
          {atts.map(att => {
            const isImage = att.mimeType?.startsWith('image/');
            const isVideo = att.mimeType?.startsWith('video/');
            const url     = fileViewUrl(att.key);
            return (
              <div key={att.key} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', position: 'relative', background: 'var(--surface-2)' }}>
                {isImage ? (
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt={att.name} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}/>
                  </a>
                ) : (
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 100, textDecoration: 'none', gap: 6 }}>
                    {attIcon(att.mimeType)}
                    <span style={{ fontSize: 10, color: 'var(--ink-40)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                      {isVideo ? 'video' : att.name?.split('.').pop()?.toUpperCase()}
                    </span>
                  </a>
                )}
                <div style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ flex: 1, fontSize: 10, color: 'var(--ink-60)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</div>
                  <button type="button"
                    onClick={() => handleDelete(att.key)}
                    disabled={deleting === att.key}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rose)', padding: '1px 3px', fontSize: 14, lineHeight: 1, flexShrink: 0 }}
                  >×</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────
export default function Inventory() {
  const { t }    = useTranslation();
  const { can }  = usePermissions();
  const canCreate = can('inventory', 'create');
  const canEdit   = can('inventory', 'edit');
  const canDelete = can('inventory', 'delete');
  const [items,    setItems]    = useState([]);
  const [props,    setProps]    = useState([]);
  const [apts,     setApts]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [propF,    setPropF]    = useState('');
  const [aptF,     setAptF]     = useState('');
  const [catF,     setCatF]     = useState('');
  const [condF,    setCondF]    = useState('');
  const [lowOnly,  setLowOnly]  = useState(false);
  const [modal,    setModal]    = useState(false);
  const [edit,     setEdit]     = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [formApts, setFormApts] = useState([]);
  const [report,     setReport]     = useState(null);
  const [reportApt,  setReportApt]  = useState('');
  const [loadRep,    setLoadRep]    = useState(false);
  const inspectorRef = useRef();
  const tenantRef    = useRef();

  // Batch entry state
  const [batchModal,  setBatchModal]  = useState(false);
  const [batchPropId, setBatchPropId] = useState('');
  const [batchAptId,  setBatchAptId]  = useState('');
  const [batchApts,   setBatchApts]   = useState([]);
  const [batchRows,   setBatchRows]   = useState([]);
  const [batchSaving, setBatchSaving] = useState(false);
  const [batchStep,   setBatchStep]   = useState(1);
  const [savingDoc,    setSavingDoc]   = useState(false);
  const [attItem,      setAttItem]     = useState(null);
  const batchManagerSigRef = useRef();
  const batchTenantSigRef  = useRef();

  const getSig = (ref) => {
    const c = ref?.current;
    if (!c) return null;
    const d = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i] < 200 && d[i + 3] > 10) return c.toDataURL('image/png');
    }
    return null;
  };

  const [allApts, setAllApts] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    const q = {};
    if (propF)   q.propertyId  = propF;
    if (aptF)    q.apartmentId = aptF;
    if (catF)    q.category    = catF;
    if (condF)   q.condition   = condF;
    if (lowOnly) q.lowStock    = 'true';
    Promise.all([inventoryAPI.getAll(q), propertiesAPI.getAll(), apartmentsAPI.getAll()])
      .then(([i, p, a]) => { setItems(i.data.data); setProps(p.data.data); setAllApts(a.data.data); })
      .catch(() => toast.error(t('failedToLoad')))
      .finally(() => setLoading(false));
  }, [propF, aptF, catF, condF, lowOnly, t]);

  // Load apartments when property filter changes
  useEffect(() => {
    if (!propF) { setApts([]); setAptF(''); return; }
    apartmentsAPI.getAll({ propertyId: propF })
      .then(r => setApts(r.data.data))
      .catch(() => {});
  }, [propF]);

  useEffect(() => { load(); }, [load]);

  // Load apartments for the add/edit form when property changes
  useEffect(() => {
    if (!form.propertyId) { setFormApts([]); return; }
    apartmentsAPI.getAll({ propertyId: form.propertyId })
      .then(r => setFormApts(r.data.data))
      .catch(() => {});
  }, [form.propertyId]);

  // Load apartments for batch modal when batch property changes
  useEffect(() => {
    if (!batchPropId) { setBatchApts([]); setBatchAptId(''); return; }
    apartmentsAPI.getAll({ propertyId: batchPropId })
      .then(r => setBatchApts(r.data.data))
      .catch(() => {});
  }, [batchPropId]);

  const openBatch = () => {
    setBatchPropId(props[0]?._id || '');
    setBatchAptId('');
    setBatchRows([{ id: Date.now(), name:'', category:'Kitchen', condition:'good', quantity:1, unit:'units', notes:'' }]);
    setBatchStep(1);
    setBatchModal(true);
  };

  const loadTemplate = () => {
    setBatchRows(UNIT_TEMPLATE.map(({ nameKey, ...r }, i) => ({ ...r, id: Date.now() + i, name: t(nameKey) })));
  };

  const addBatchRow = () => {
    setBatchRows(rows => [...rows, { id: Date.now(), name:'', category:'Kitchen', condition:'good', quantity:1, unit:'units', notes:'' }]);
  };

  const updateBatchRow = (id, field, value) => {
    setBatchRows(rows => rows.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const removeBatchRow = id => {
    setBatchRows(rows => rows.filter(r => r.id !== id));
  };

  const handleBatchSave = async () => {
    if (!batchPropId) return toast.warning(t('property') + ' ' + t('required'));
    const valid = batchRows.filter(r => r.name.trim());
    if (!valid.length) return toast.warning(t('itemName') + ' ' + t('required'));
    setBatchSaving(true);
    try {
      const items = valid.map(({ id: _id, ...r }) => ({
        ...r,
        quantity: Number(r.quantity) || 1,
        minStock: 0,
        cost: 0,
      }));
      await inventoryAPI.batch({ propertyId: batchPropId, apartmentId: batchAptId || undefined, items });

      const mSig = getSig(batchManagerSigRef);
      const tSig = getSig(batchTenantSigRef);
      if (batchAptId && (mSig || tSig)) {
        await inspectionsAPI.create({
          propertyId:       batchPropId,
          apartmentId:      batchAptId,
          managerSignature: mSig,
          tenantSignature:  tSig,
        });
        toast.success(t('inspectionSaved'));
      } else {
        toast.success(t('itemsAdded', { count: items.length }));
      }

      setBatchModal(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBatchSaving(false); }
  };

  const handleSaveDocument = async () => {
    const element = document.getElementById('unit-report-print');
    if (!element) return;
    setSavingDoc(true);
    try {
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * pageW) / canvas.width;
      let y = 0;
      while (y < imgH) {
        if (y > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -y, pageW, imgH);
        y += pageH;
      }
      const blob = pdf.output('blob');
      const apt = report.apartment;
      const dateStr = new Date().toISOString().slice(0, 10);
      const fd = new FormData();
      fd.append('file', blob, `inspection-unit${apt.unit}-${dateStr}.pdf`);
      fd.append('title', `${t('unitInspectionReport')} #${apt.unit} — ${dateStr}`);
      fd.append('category', 'maintenance');
      if (apt.propertyId?._id) fd.append('propertyId', apt.propertyId._id);
      await documentsAPI.upload(fd);
      toast.success(t('documentSaved'));
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSavingDoc(false); }
  };

  const filtered = items.filter(i =>
    !search || i.name.toLowerCase().includes(search.toLowerCase()) || i.supplier?.toLowerCase().includes(search.toLowerCase())
  );
  const lowCount = items.filter(i => i.quantity <= i.minStock).length;
  const totalVal = items.reduce((s, i) => s + (i.quantity * i.cost), 0);

  const openAdd  = () => { setForm({ ...EMPTY, propertyId: props[0]?._id || '' }); setEdit(null); setModal(true); };
  const openEdit = i  => {
    setEdit(i._id);
    setForm({
      ...i,
      propertyId:  i.propertyId?._id  || i.propertyId  || '',
      apartmentId: i.apartmentId?._id || i.apartmentId || '',
    });
    setModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.propertyId) return toast.warning(t('itemName') + ' & ' + t('property') + ' ' + t('required'));
    setSaving(true);
    try {
      const data = {
        ...form,
        quantity: Number(form.quantity) || 0,
        minStock: Number(form.minStock) || 0,
        cost:     Number(form.cost)     || 0,
      };
      if (!data.apartmentId) delete data.apartmentId;
      if (edit) { await inventoryAPI.update(edit, data); toast.success(t('itemUpdated')); }
      else      { await inventoryAPI.create(data);       toast.success(t('itemAdded')); }
      setModal(false); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm(t('removeItem'))) return;
    try { await inventoryAPI.delete(id); toast.success(t('itemRemoved')); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const openReport = async () => {
    if (!reportApt) return toast.warning('Select a unit first');
    setLoadRep(true);
    try {
      const { data } = await inventoryAPI.unitReport(reportApt);
      setReport(data.data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoadRep(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('inventory')}</h1>
          <p className="page-subtitle">{t('propertySuppliesStock')}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Quick unit report launcher */}
          <select className="form-input form-select" style={{ minWidth: 160 }} value={reportApt} onChange={e => setReportApt(e.target.value)}>
            <option value="">{t('allUnits')}</option>
            {props.map(p => {
              const propApts = allApts.filter(a => (a.propertyId?._id || a.propertyId) === p._id);
              if (!propApts.length) return null;
              return (
                <optgroup key={p._id} label={p.name}>
                  {propApts.map(a => <option key={a._id} value={a._id}>{p.name} #{a.unit}</option>)}
                </optgroup>
              );
            })}
          </select>
          <button className="btn btn-secondary" onClick={openReport} disabled={!reportApt || loadRep}>
            <FileText size={15}/>{t('generateUnitReport')}
          </button>
          <button className="btn btn-secondary btn-icon" onClick={load}><RefreshCw size={16}/></button>
          {canCreate && <button className="btn btn-secondary" onClick={openBatch}><Package size={15}/>{t('addItemsToUnit')}</button>}
          {canCreate && <button className="btn btn-primary" onClick={openAdd}><Plus size={16}/>{t('addInventoryItem')}</button>}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-3 mb-6">
        {[
          { label: t('totalItems'),    value: items.length,            color: 'var(--brand)' },
          { label: t('lowStockItems'), value: lowCount,                color: 'var(--amber)' },
          { label: t('totalValue'),    value: fmt.currency(totalVal),  color: 'var(--emerald)' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-40)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 26, fontWeight: 800, fontFamily: 'var(--font-display)', color: s.color }}>{s.value}</div>
            </div>
            <Package size={28} color={s.color} style={{ opacity: .2 }}/>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 180 }}>
          <Search size={15} color="var(--ink-20)"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('searchItemsSuppliers')}/>
        </div>
        <select className="form-input form-select" style={{ flex: '1 1 140px', minWidth: 120 }} value={propF} onChange={e => { setPropF(e.target.value); setAptF(''); }}>
          <option value="">{t('allProperties')}</option>
          {props.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
        {propF && (
          <select className="form-input form-select" style={{ flex: '1 1 130px', minWidth: 110 }} value={aptF} onChange={e => setAptF(e.target.value)}>
            <option value="">{t('allUnits')}</option>
            {apts.map(a => <option key={a._id} value={a._id}>#{a.unit}</option>)}
          </select>
        )}
        <select className="form-input form-select" style={{ flex: '1 1 140px', minWidth: 120 }} value={catF} onChange={e => setCatF(e.target.value)}>
          <option value="">{t('allCategories')}</option>
          <optgroup label="— Unit —">
            {UNIT_CATS.map(c => <option key={c} value={c}>{tCat(t, c)}</option>)}
          </optgroup>
          <optgroup label="— Supplies —">
            {SUPPLY_CATS.map(c => <option key={c} value={c}>{tCat(t, c)}</option>)}
          </optgroup>
        </select>
        <select className="form-input form-select" style={{ flex: '1 1 120px', minWidth: 110 }} value={condF} onChange={e => setCondF(e.target.value)}>
          <option value="">{t('condition')}</option>
          {CONDITIONS.map(c => <option key={c} value={c}>{tCond(t, c)}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={lowOnly} onChange={e => setLowOnly(e.target.checked)}/>
          <AlertTriangle size={13} color="var(--amber)"/>{t('lowStockOnly')}
        </label>
      </div>

      {/* Table */}
      <div className="card">
        <div style={{ overflowX: 'auto' }}>
          {loading ? <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }}/></div> : (
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 1, whiteSpace: 'nowrap' }}></th>
                  <th>{t('itemName')}</th>
                  <th>{t('unit')}</th>
                  <th>{t('category')}</th>
                  <th>{t('condition')}</th>
                  <th>{t('qty')}</th>
                  <th>{t('unitCost')}</th>
                  <th>{t('totalValue')}</th>
                  <th>{t('supplier')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(i => {
                  const isLow = i.quantity <= i.minStock;
                  return (
                    <tr key={i._id} style={isLow ? { background: 'rgba(245,158,11,0.03)' } : {}}>
                      <td className="table-actions">
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setAttItem(i)} title={t('attachments')}>
                            <Paperclip size={13}/>
                            {i.attachments?.length > 0 && <span style={{ fontSize:10, marginLeft:2, color:'var(--brand)', fontWeight:700 }}>{i.attachments.length}</span>}
                          </button>
                          {canEdit   && <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openEdit(i)} title={t('edit')}><Edit2 size={13}/></button>}
                          {canDelete && <button className="btn btn-secondary btn-sm btn-icon" style={{ color: 'var(--rose)' }} onClick={() => handleDelete(i._id)} title={t('delete')}><Trash2 size={13}/></button>}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {isLow && <AlertTriangle size={13} color="var(--amber)"/>}
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{i.name}</div>
                            {i.notes && <div style={{ fontSize: 11, color: 'var(--ink-40)' }}>{i.notes}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {i.apartmentId ? <span style={{ fontWeight: 600 }}>#{i.apartmentId.unit}</span> : <span style={{ color: 'var(--ink-20)' }}>—</span>}
                        <div style={{ fontSize: 11, color: 'var(--ink-40)' }}>{i.propertyId?.name}</div>
                      </td>
                      <td><span className="badge badge-neutral">{tCat(t, i.category)}</span></td>
                      <td>
                        <span style={{ fontSize: 12, fontWeight: 700, color: COND_COLOR[i.condition] }}>
                          {tCond(t, i.condition)}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontWeight: 700, fontSize: 15, color: isLow ? 'var(--rose)' : 'var(--ink)' }}>{i.quantity}</span>
                        <span style={{ fontSize: 11, color: 'var(--ink-40)', marginLeft: 3 }}>{i.unit}</span>
                      </td>
                      <td style={{ fontSize: 13 }}>{fmt.currencyFull(i.cost)}</td>
                      <td style={{ fontSize: 13, fontWeight: 600 }}>{fmt.currency(i.quantity * i.cost)}</td>
                      <td style={{ fontSize: 12 }}>{i.supplier || '—'}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: 'var(--ink-40)' }}>{t('noItemsFound')}</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={edit ? t('editItem') : t('addInventoryItem')} size="lg"
        footer={
          <><button className="btn btn-secondary" onClick={() => setModal(false)}>{t('cancel')}</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('saving') : t('save')}</button></>
        }>
        <div className="form-grid form-grid-2" style={{ gap: 16 }}>
          <div className="form-group" style={{ gridColumn: '1/-1' }}>
            <label className="form-label">{t('itemName')} *</label>
            <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t('exampleHVAC')}/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('property')} *</label>
            <select className="form-input form-select" value={form.propertyId} onChange={e => setForm(f => ({ ...f, propertyId: e.target.value, apartmentId: '' }))}>
              <option value="">{t('select')}</option>
              {props.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('unit')} ({t('optional')})</label>
            <select className="form-input form-select" value={form.apartmentId} onChange={e => setForm(f => ({ ...f, apartmentId: e.target.value }))} disabled={!form.propertyId}>
              <option value="">{t('allUnits')}</option>
              {formApts.map(a => <option key={a._id} value={a._id}>#{a.unit}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('category')}</label>
            <select className="form-input form-select" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              <optgroup label="— Unit —">
                {UNIT_CATS.map(c => <option key={c} value={c}>{tCat(t, c)}</option>)}
              </optgroup>
              <optgroup label="— Supplies —">
                {SUPPLY_CATS.map(c => <option key={c} value={c}>{tCat(t, c)}</option>)}
              </optgroup>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('condition')}</label>
            <select className="form-input form-select" value={form.condition} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))}>
              {CONDITIONS.map(c => (
                <option key={c} value={c}>{tCond(t, c)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('quantity')}</label>
            <input className="form-input" type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="1"/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('unit')}</label>
            <input className="form-input" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder={t('unitsGallons')}/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('minStockLevel')}</label>
            <input className="form-input" type="number" value={form.minStock} onChange={e => setForm(f => ({ ...f, minStock: e.target.value }))} placeholder="0"/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('unitCostDollar')}</label>
            <input className="form-input" type="number" step="0.01" value={form.cost} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} placeholder="0.00"/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('supplier')}</label>
            <input className="form-input" value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} placeholder={t('exampleFilterMax')}/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('storageLocation')}</label>
            <input className="form-input" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder={t('exampleStorageRoom')}/>
          </div>
          <div className="form-group" style={{ gridColumn: '1/-1' }}>
            <label className="form-label">{t('notes')}</label>
            <textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder={t('optional')}/>
          </div>
        </div>
      </Modal>

      {/* Batch Entry Modal */}
      <Modal open={batchModal} onClose={() => { setBatchModal(false); setBatchStep(1); }} title={t('batchModalTitle')} size="xl"
        footer={
          batchStep === 1
            ? <><button className="btn btn-secondary" onClick={() => setBatchModal(false)}>{t('cancel')}</button>
                <button className="btn btn-primary" onClick={() => { if (!batchPropId) return toast.warning(t('property') + ' ' + t('required')); if (!batchRows.filter(r => r.name.trim()).length) return toast.warning(t('itemName') + ' ' + t('required')); setBatchStep(2); }}>
                  {t('nextSignatures')} →
                </button></>
            : <><button className="btn btn-secondary" onClick={() => setBatchStep(1)}>{t('backToItems')}</button>
                <button className="btn btn-primary" onClick={handleBatchSave} disabled={batchSaving}>{batchSaving ? t('saving') : t('saveAll')}</button></>
        }>
        {/* Step 1 — Items */}
        {/* Property + Unit selector */}
        <div style={{ display: batchStep === 2 ? 'none' : 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <div className="form-group" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
            <label className="form-label">{t('property')} *</label>
            <select className="form-input form-select" value={batchPropId} onChange={e => setBatchPropId(e.target.value)}>
              <option value="">{t('select')}</option>
              {props.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
            <label className="form-label">{t('unit')} ({t('optional')})</label>
            <select className="form-input form-select" value={batchAptId} onChange={e => setBatchAptId(e.target.value)} disabled={!batchPropId}>
              <option value="">{t('allUnits')}</option>
              {batchApts.map(a => <option key={a._id} value={a._id}>#{a.unit}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={loadTemplate} title="Pre-fill standard unit items">
              <RefreshCw size={14}/> {t('loadTemplate')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={addBatchRow}>
              <Plus size={14}/> {t('addRow')}
            </button>
          </div>
        </div>

        {/* Rows table */}
        <div style={{ overflowX: 'auto', display: batchStep === 2 ? 'none' : 'block' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-60)', fontSize: 11, whiteSpace: 'nowrap' }}>{t('itemName')} *</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-60)', fontSize: 11 }}>{t('category')}</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-60)', fontSize: 11 }}>{t('condition')}</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-60)', fontSize: 11, width: 60 }}>{t('qty')}</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-60)', fontSize: 11 }}>{t('notes')}</th>
                <th style={{ width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {batchRows.map((row, idx) => (
                <tr key={row.id} style={{ borderBottom: '1px solid var(--border)', background: idx % 2 === 0 ? 'transparent' : 'var(--surface-2)' }}>
                  <td style={{ padding: '4px 6px' }}>
                    <input
                      className="form-input"
                      style={{ padding: '5px 8px', fontSize: 13, minWidth: 150 }}
                      value={row.name}
                      onChange={e => updateBatchRow(row.id, 'name', e.target.value)}
                      placeholder={t('itemName')}
                    />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <select
                      className="form-input form-select"
                      style={{ padding: '5px 8px', fontSize: 12, minWidth: 140 }}
                      value={row.category}
                      onChange={e => updateBatchRow(row.id, 'category', e.target.value)}
                    >
                      <optgroup label="— Unit —">
                        {UNIT_CATS.map(c => <option key={c} value={c}>{tCat(t, c)}</option>)}
                      </optgroup>
                      <optgroup label="— Supplies —">
                        {SUPPLY_CATS.map(c => <option key={c} value={c}>{tCat(t, c)}</option>)}
                      </optgroup>
                    </select>
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <select
                      className="form-input form-select"
                      style={{ padding: '5px 8px', fontSize: 12, minWidth: 90 }}
                      value={row.condition}
                      onChange={e => updateBatchRow(row.id, 'condition', e.target.value)}
                    >
                      {CONDITIONS.map(c => (
                        <option key={c} value={c} style={{ color: COND_COLOR[c] }}>{tCond(t, c)}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input
                      className="form-input"
                      style={{ padding: '5px 8px', fontSize: 13, width: 60 }}
                      type="number"
                      min="0"
                      value={row.quantity}
                      onChange={e => updateBatchRow(row.id, 'quantity', e.target.value)}
                    />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input
                      className="form-input"
                      style={{ padding: '5px 8px', fontSize: 12, minWidth: 120 }}
                      value={row.notes}
                      onChange={e => updateBatchRow(row.id, 'notes', e.target.value)}
                      placeholder={t('optional')}
                    />
                  </td>
                  <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => removeBatchRow(row.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rose)', fontSize: 16, lineHeight: 1, padding: '2px 4px' }}
                      title={t('delete')}
                    >×</button>
                  </td>
                </tr>
              ))}
              {batchRows.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--ink-40)', fontSize: 13 }}>
                    {t('selectPropertyAndUnit')} · {t('loadTemplate')} / {t('addRow')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {batchStep === 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 12, color: 'var(--ink-40)' }}>
            <span>{batchRows.filter(r => r.name.trim()).length} / {batchRows.length} {t('itemName').toLowerCase()}s</span>
            <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} onClick={addBatchRow}>
              <Plus size={13}/> {t('addRow')}
            </button>
          </div>
        )}

        {/* Step 2 — Signatures */}
        {batchStep === 2 && (
          <div style={{ marginTop: 24, borderTop: '2px dashed var(--border)', paddingTop: 20 }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-80)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{t('signaturesRequired')}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-40)', marginTop: 4 }}>{t('signBeforeSaving')}</div>
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              <SignatureCanvas label={t('managerSignature')} canvasRef={batchManagerSigRef}/>
              <SignatureCanvas label={t('tenantSignature')}  canvasRef={batchTenantSigRef}/>
            </div>
          </div>
        )}
      </Modal>

      {/* Attachment Manager Modal */}
      <Modal open={!!attItem} onClose={() => setAttItem(null)}
        title={attItem ? `${t('attachments')} — ${attItem.name}` : ''}
        size="lg"
        footer={<button className="btn btn-secondary" onClick={() => setAttItem(null)}>{t('close')}</button>}>
        {attItem && (
          <AttachmentManager
            item={attItem}
            t={t}
            onUpdated={updated => {
              setAttItem(updated);
              setItems(prev => prev.map(x => x._id === updated._id ? { ...x, attachments: updated.attachments } : x));
            }}
          />
        )}
      </Modal>

      {/* Unit Report Modal */}
      <Modal open={!!report} onClose={() => setReport(null)} title={t('unitInventoryReport')} size="lg"
        footer={
          <><button className="btn btn-secondary" onClick={() => setReport(null)}>{t('close')}</button>
          <button className="btn btn-secondary" onClick={handleSaveDocument} disabled={savingDoc}>
            <Save size={14}/>{savingDoc ? t('savingDocument') : t('saveDocument')}
          </button>
          <button className="btn btn-primary" onClick={() => window.print()}><FileText size={14}/>{t('printReport')}</button></>
        }>
        {report && <UnitReportView data={report} t={t} inspectorRef={inspectorRef} tenantRef={tenantRef}/>}
      </Modal>
    </div>
  );
}
